import React, { useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  useSpace, useItemAssets, useItemUploads, deleteItemUpload, updateSpace,
} from '../data/firestore';
import { downloadAsset } from '../data/uploadService';
import Uploader from '../components/Uploader';
import DropLinkManager from '../components/DropLinkManager';
import Collapsible from '../components/Collapsible';
import Moodboards from '../components/Moodboards';
import SpaceLists from '../components/SpaceLists';
import AssetImage from '../components/AssetImage';
import { useAuth } from '../AuthContext';

// One renovation space's workspace: photos & files (owner uploads + contractor
// drop links), moodboards, lists (furniture/todo/shopping) and notes.

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
};

const metaBadge = (a) => {
  const st = a.meta?.status || 'none';
  const color = st === 'done' ? '#7ce38b' : st === 'error' ? 'var(--danger, #ff6b6b)' : st === 'none' ? 'var(--muted)' : '#ffd166';
  const label = st === 'done' ? 'verified' : st === 'running' ? 'verifying…' : st;
  return <span style={{ fontSize: 11, color }} title={a.meta?.message || ''}>{label}</span>;
};

const STATUSES = [
  { id: 'planning', label: 'Planning' },
  { id: 'in-progress', label: 'In progress' },
  { id: 'done', label: 'Done' },
];

const TABS = [
  { id: 'files', label: 'Photos & files' },
  { id: 'moodboards', label: 'Moodboards' },
  { id: 'lists', label: 'Lists' },
  { id: 'notes', label: 'Notes' },
];

function FilesTab({ space, assets, uploads, canEdit }) {
  // Live in-flight guest uploads (mirrored by the backend from drop heartbeats).
  const liveUploads = (uploads || []).filter(
    (u) => u.status === 'uploading' && Date.now() - (u.updatedMs || 0) < 5 * 60 * 1000
  );
  const photos = (assets || []).filter((a) => a.kind === 'photo' && a.s3Key);
  const files = (assets || []).filter((a) => !(a.kind === 'photo' && a.s3Key));

  return (
    <>
      {canEdit && (
        <>
          <Collapsible title="Upload files" defaultOpen={!(assets || []).length}>
            <Uploader item={space} />
          </Collapsible>

          <Collapsible title="Contractor / designer upload links">
            <DropLinkManager item={space} />
          </Collapsible>
        </>
      )}

      {canEdit && liveUploads.map((u) => (
        <div key={u.id} className="cs-card" style={{ padding: 10, marginTop: 10, opacity: 0.85 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13 }}>
            <span>⬆ {u.name}</span>
            <span className="cs-help" style={{ fontSize: 12 }}>
              {u.contributor ? `${u.contributor} · ` : ''}uploading now · {u.pct || 0}%
            </span>
            <span style={{ flex: 1 }} />
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => deleteItemUpload(space.id, u.id)}>Dismiss</button>
          </div>
          <div className="cs-progress" style={{ height: 5, marginTop: 6 }}>
            <div style={{ width: `${u.pct || 0}%` }} />
          </div>
        </div>
      ))}

      {assets && !assets.length && !liveUploads.length && (
        <div className="cs-empty" style={{ marginTop: 14 }}>
          No photos or files yet — upload current-state photos, plans, quotes…
        </div>
      )}

      {photos.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ fontSize: 15, margin: '0 0 8px' }}>Photos</h3>
          <div className="ah-photo-grid">
            {photos.map((a) => (
              <figure
                key={a.id}
                className="ah-photo"
                title={canEdit ? `${a.name} — click to download` : a.name}
                onClick={canEdit ? () => downloadAsset({ key: a.s3Key, name: a.name }) : undefined}
              >
                <AssetImage s3Key={a.s3Key} alt={a.name} />
                <figcaption>{a.name}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      )}

      {files.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ fontSize: 15, margin: '0 0 8px' }}>Files</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {files.map((a) => (
              <div key={a.id} className="cs-card" style={{ padding: 12 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>{a.kind === 'folder' ? '📁' : '📄'}</span>
                  <strong style={{ fontSize: 13 }}>{a.name}</strong>
                  {metaBadge(a)}
                  <span className="cs-help" style={{ fontSize: 12 }}>
                    {fmtBytes(a.sizeBytes)}
                    {Array.isArray(a.parts) && a.parts.length > 1 ? ` · ${a.parts.length} files` : ''}
                    {a.contributor ? ` · from ${a.contributor}` : ''}
                  </span>
                  <span style={{ flex: 1 }} />
                  {canEdit && (
                    <button
                      className="cs-btn cs-btn-ghost cs-btn-sm"
                      onClick={() => downloadAsset({ key: a.s3Key, name: a.name })}
                      title="Presigned download from the private raw bucket"
                    >
                      Download
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function NotesTab({ space, canEdit }) {
  const [notes, setNotes] = useState(space.notes || '');
  const [saved, setSaved] = useState(true);
  if (!canEdit) {
    return space.notes
      ? <p style={{ maxWidth: 720, whiteSpace: 'pre-wrap' }}>{space.notes}</p>
      : <div className="cs-empty">No notes yet.</div>;
  }
  return (
    <div style={{ maxWidth: 720 }}>
      <textarea
        className="cs-input"
        rows={14}
        style={{ width: '100%', resize: 'vertical' }}
        placeholder="Scope, measurements, contractor quotes, decisions…"
        value={notes}
        onChange={(e) => { setNotes(e.target.value); setSaved(false); }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <button
          className="cs-btn cs-btn-primary cs-btn-sm"
          disabled={saved}
          onClick={async () => { await updateSpace(space.id, { notes }); setSaved(true); }}
        >
          {saved ? 'Saved' : 'Save notes'}
        </button>
      </div>
    </div>
  );
}

export default function SpaceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = !!user;
  const { doc: space } = useSpace(id);
  const { docs: assets } = useItemAssets(id);
  const { docs: uploads } = useItemUploads(id);
  // ?tab=moodboards etc. lets other pages (Designs gallery) deep-link a tab.
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const [tab, setTab] = useState(TABS.some((t) => t.id === requested) ? requested : 'files');

  if (space === undefined) return <div className="cs-page"><p className="cs-help">Loading…</p></div>;
  if (space === null) {
    return (
      <div className="cs-page">
        <p className="cs-help">Space not found. <Link to="/">Back to the house</Link></p>
      </div>
    );
  }

  return (
    <div className="cs-page">
      <p style={{ margin: 0 }}><Link to="/" className="cs-help">← Amalfi House</Link></p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
        <h1 style={{ margin: 0 }}>{space.icon} {space.name}</h1>
        {canEdit ? (
          <select
            className="cs-input ah-status-select"
            value={space.status || 'planning'}
            onChange={(e) => updateSpace(space.id, { status: e.target.value })}
          >
            {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        ) : (
          <span className="ah-status" style={{ fontSize: 12 }}>
            {(STATUSES.find((s) => s.id === space.status) || STATUSES[0]).label}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {canEdit && (
          <label className="cs-help" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            Budget $
            <input
              className="cs-input ah-budget-input"
              type="number"
              min="0"
              defaultValue={space.budget || ''}
              onBlur={(e) => updateSpace(space.id, { budget: Number(e.target.value) || 0 })}
            />
          </label>
        )}
      </div>
      {space.description && <p className="cs-help">{space.description}</p>}

      <div className="ah-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`ah-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 18 }}>
        {tab === 'files' && <FilesTab space={space} assets={assets} uploads={uploads} canEdit={canEdit} />}
        {tab === 'moodboards' && <Moodboards space={space} canEdit={canEdit} />}
        {tab === 'lists' && <SpaceLists space={space} canEdit={canEdit} />}
        {tab === 'notes' && <NotesTab space={space} canEdit={canEdit} />}
      </div>
    </div>
  );
}
